/**
 * End outer-island helpers with a runtime side: eating chorus fruit teleports
 * the player a short hop, and End Gateways (placed after the dragon dies,
 * worldgen/end.ts buildEndGateway) send whoever walks into them to an outer
 * island. DOM-free; main.ts supplies the host.
 */

import { AIR, END_GATEWAY_ID, IS_SOLID } from '../blocks';
import { END_GATEWAY_COUNT, endGatewayExit, endGatewayPos } from '../worldgen/end';

export interface OuterHost {
  getBlock(x: number, y: number, z: number): number;
  teleport(x: number, y: number, z: number): void;
  /** Warp far away: the host freezes the player until chunks at (x, z) load, then drops them on the surface. */
  warp(x: number, z: number, y?: number): void;
  seed: number;
  rnd(): number;
}

/** Vanilla chorus fruit: up to 16 tries at a random spot within 8 blocks, standing on something with 2 air above. */
export function chorusTeleport(h: OuterHost, px: number, py: number, pz: number): boolean {
  for (let k = 0; k < 16; k++) {
    const x = Math.floor(px + (h.rnd() - 0.5) * 16), z = Math.floor(pz + (h.rnd() - 0.5) * 16);
    const y0 = Math.floor(py + (h.rnd() - 0.5) * 16);
    for (let y = Math.min(y0, 190); y > 1; y--) {
      if (IS_SOLID[h.getBlock(x, y - 1, z)] === 1) {
        if (h.getBlock(x, y, z) === AIR && h.getBlock(x, y + 1, z) === AIR) { h.teleport(x + 0.5, y, z + 0.5); return true; }
        break;
      }
    }
  }
  return false;
}

/** Index of the gateway whose block is at (x, y, z), or -1. */
export function gatewayIndexAt(x: number, y: number, z: number): number {
  for (let i = 0; i < END_GATEWAY_COUNT; i++) {
    const g = endGatewayPos(i);
    if (g.x === x && g.y === y && g.z === z) return i;
  }
  return -1;
}

export class GatewayRuntime {
  private cooldown = 0;
  constructor(private readonly h: OuterHost) {}

  /** A thrown pearl entered the gateway block at (x, y, z): warp the player. */
  enterAt(x: number, y: number, z: number): boolean {
    if (this.h.getBlock(x, y, z) !== END_GATEWAY_ID) return false;
    const i = gatewayIndexAt(x, y, z);
    if (i < 0) return false;
    const to = endGatewayExit(this.h.seed, i);
    this.cooldown = 100;
    this.h.warp(to.x + 0.5, to.z + 0.5);
    return true;
  }

  /** Per tick while in the End: stepping into a gateway block warps to its outer island. */
  tick(px: number, py: number, pz: number): boolean {
    if (this.cooldown > 0) { this.cooldown--; return false; }
    const x = Math.floor(px), z = Math.floor(pz);
    for (const dy of [0, 1]) {
      const y = Math.floor(py) + dy;
      if (this.h.getBlock(x, y, z) !== END_GATEWAY_ID) continue;
      // the index is the ring slot; a gateway cell is the middle of its column
      const i = gatewayIndexAt(x, y, z);
      if (i < 0) continue;
      const to = endGatewayExit(this.h.seed, i);
      this.cooldown = 100;
      this.h.warp(to.x + 0.5, to.z + 0.5);
      return true;
    }
    return false;
  }
}
